import type { ApiClient } from "./api-client.js";

const APP_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MAX_PROVIDER_TICKET_LENGTH = 4096;

export type AccountSetupPhase =
  | "captured"
  | "resolved"
  | "code_sent"
  | "code_verified"
  | "ticket_issued"
  | "ticket_created"
  | "session_active"
  | "bound"
  | "accepted"
  | "terminal";

interface InvitationSecretState {
  appToken: string | null;
  challengeId: string | null;
  flowNonce: string | null;
  exchangeSecret: string | null;
  providerTicket: string | null;
  setupSessionId: string | null;
  phase: AccountSetupPhase;
  accountName: string | null;
  emailHint: string | null;
  role: "owner" | "admin" | "member" | "viewer" | null;
  invitationExpiresAt: string | null;
  codeExpiresAt: string | null;
  providerExpiresAt: string | null;
  passwordSaveAttempted: boolean;
}

export interface AccountSetupSnapshot {
  phase: AccountSetupPhase | "empty";
  accountName: string | null;
  emailHint: string | null;
  role: InvitationSecretState["role"];
  invitationExpiresAt: string | null;
  codeExpiresAt: string | null;
  providerExpiresAt: string | null;
  passwordSaveAttempted: boolean;
}

interface BrowserLocationLike {
  pathname: string;
  search: string;
  hash: string;
}

interface BrowserHistoryLike {
  state: unknown;
  replaceState: (data: unknown, unused: string, url?: string | URL | null) => void;
}

interface InvitationBrowserLike {
  location: BrowserLocationLike;
  history: BrowserHistoryLike;
  __takeAccountSetupInvitation?: () => unknown;
}

interface ResolvedSetupResponse {
  accountName: string;
  emailHint: string;
  role: "owner" | "admin" | "member" | "viewer";
  expiresAt: string;
  codeSentAt: string | null;
  codeExpiresAt: string | null;
}

interface ChallengeResponse {
  challengeId: string;
  flowNonce: string;
  expiresAt: string;
  deliveryConfirmed: boolean;
}

interface VerifiedResponse {
  challengeId: string;
  exchangeSecret: string;
  expiresAt: string;
}

interface ExchangedResponse {
  mode: "password_setup";
  providerTicket: string;
  expiresAt: string;
}

interface BoundResponse {
  ok: true;
  accountName: string;
}

export interface AcceptedSetupResponse {
  ok: true;
  accountId: string;
  accountName: string;
  role: "owner" | "admin" | "member" | "viewer";
  alreadyMember: boolean;
}

export interface TicketSignInLike {
  create: (input: { strategy: "ticket"; ticket: string }) => Promise<{
    status: string | null;
    createdSessionId?: string | null;
  }>;
}

export type SetActiveLike = (input: { session: string }) => Promise<unknown>;

let secretState: InvitationSecretState | null = null;
const inFlight = new Map<string, Promise<unknown>>();

function newCapturedState(appToken: string): InvitationSecretState {
  return {
    appToken,
    challengeId: null,
    flowNonce: null,
    exchangeSecret: null,
    providerTicket: null,
    setupSessionId: null,
    phase: "captured",
    accountName: null,
    emailHint: null,
    role: null,
    invitationExpiresAt: null,
    codeExpiresAt: null,
    providerExpiresAt: null,
    passwordSaveAttempted: false,
  };
}

function appTokenFromExactFragment(hash: string): string | null {
  const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
  const keys = [...params.keys()];
  if (keys.length !== 1 || keys[0] !== "invite") return null;
  const values = params.getAll("invite");
  const value = values.length === 1 ? values[0] ?? null : null;
  return value !== null && APP_TOKEN_PATTERN.test(value) ? value : null;
}

function validDateString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0 && Number.isFinite(Date.parse(value));
}

function validRole(value: unknown): value is NonNullable<InvitationSecretState["role"]> {
  return value === "owner" || value === "admin" || value === "member" || value === "viewer";
}

function requireState(): InvitationSecretState {
  if (secretState === null) throw new Error("Invitation setup is not available");
  return secretState;
}

function requireAppToken(): { state: InvitationSecretState; appToken: string } {
  const state = requireState();
  if (state.appToken === null) throw new Error("Invitation setup is not available");
  return { state, appToken: state.appToken };
}

async function oneFlight<T>(key: string, operation: () => Promise<T>): Promise<T> {
  const current = inFlight.get(key);
  if (current) return current as Promise<T>;
  const promise = operation();
  inFlight.set(key, promise);
  try {
    return await promise;
  } finally {
    if (inFlight.get(key) === promise) inFlight.delete(key);
  }
}

/**
 * Consume the app invitation installed by the first inline head script. The
 * direct fragment parser is only a fail-safe for tests and non-HTML entry
 * points. Production HTML removes the fragment before fonts or modules load.
 */
export function captureInvitationSecretFromBrowser(browser: InvitationBrowserLike): void {
  const takeBootstrappedInvitation = browser.__takeAccountSetupInvitation;
  if (typeof takeBootstrappedInvitation === "function") {
    let bootstrapped: unknown = null;
    try {
      bootstrapped = takeBootstrappedInvitation();
    } catch {
      bootstrapped = null;
    } finally {
      try {
        delete browser.__takeAccountSetupInvitation;
      } catch {
        // The head script deletes its one-time getter on first read. This is a
        // final cleanup only if another script changed the descriptor.
      }
    }
    if (typeof bootstrapped === "string" && APP_TOKEN_PATTERN.test(bootstrapped)) {
      secretState = newCapturedState(bootstrapped);
      return;
    }
  }

  const { pathname, search, hash } = browser.location;
  const canonicalPath = pathname === "/create-account";
  if (canonicalPath && search === "" && hash !== "") {
    const appToken = appTokenFromExactFragment(hash);
    if (appToken !== null) secretState = newCapturedState(appToken);
    browser.history.replaceState(browser.history.state, "", "/create-account");
    return;
  }

  const shouldScrub = pathname === "/invite"
    || pathname.startsWith("/invite/")
    || pathname.startsWith("/create-account/")
    || (canonicalPath && (search !== "" || hash !== ""));
  if (shouldScrub) {
    browser.history.replaceState(browser.history.state, "", "/create-account");
    return;
  }
  if ((pathname === "/signup" || pathname.startsWith("/signup/")) && (search !== "" || hash !== "")) {
    browser.history.replaceState(browser.history.state, "", "/signup");
  }
}

export function accountSetupSnapshot(): AccountSetupSnapshot {
  if (secretState === null) {
    return {
      phase: "empty",
      accountName: null,
      emailHint: null,
      role: null,
      invitationExpiresAt: null,
      codeExpiresAt: null,
      providerExpiresAt: null,
      passwordSaveAttempted: false,
    };
  }
  return {
    phase: secretState.phase,
    accountName: secretState.accountName,
    emailHint: secretState.emailHint,
    role: secretState.role,
    invitationExpiresAt: secretState.invitationExpiresAt,
    codeExpiresAt: secretState.codeExpiresAt,
    providerExpiresAt: secretState.providerExpiresAt,
    passwordSaveAttempted: secretState.passwordSaveAttempted,
  };
}

export function clearInvitationSecret(): void {
  secretState = null;
  inFlight.clear();
}

export function terminalizeInvitationSecret(): void {
  const state = requireState();
  state.appToken = null;
  state.challengeId = null;
  state.flowNonce = null;
  state.exchangeSecret = null;
  state.providerTicket = null;
  state.setupSessionId = null;
  state.phase = "terminal";
}

export function restartInvitationCode(): void {
  const { state } = requireAppToken();
  state.challengeId = null;
  state.flowNonce = null;
  state.exchangeSecret = null;
  state.providerTicket = null;
  state.setupSessionId = null;
  state.codeExpiresAt = null;
  state.providerExpiresAt = null;
  state.passwordSaveAttempted = false;
  state.phase = "resolved";
}

export function markInvitationPasswordSaveAttempted(): void {
  const state = requireState();
  if (state.phase !== "bound") throw new Error("Invitation setup is not bound");
  state.passwordSaveAttempted = true;
}

export function capturedSetupSessionMatches(sessionId: string | null | undefined): boolean {
  return !!sessionId && secretState?.setupSessionId === sessionId;
}

export async function resolveCapturedInvitation(api: ApiClient): Promise<AccountSetupSnapshot> {
  return oneFlight("resolve", async () => {
    const { state, appToken } = requireAppToken();
    if (state.phase !== "captured") return accountSetupSnapshot();
    const value = await api.postPublic<unknown>("/public/account-setup/resolve", { appToken });
    if (!value || typeof value !== "object") throw new Error("Invalid account setup response");
    const response = value as Partial<ResolvedSetupResponse>;
    if (
      typeof response.accountName !== "string"
      || response.accountName.length === 0
      || typeof response.emailHint !== "string"
      || response.emailHint.length === 0
      || !validRole(response.role)
      || !validDateString(response.expiresAt)
      || (response.codeSentAt !== null && typeof response.codeSentAt !== "string")
      || (response.codeExpiresAt !== null && !validDateString(response.codeExpiresAt))
    ) throw new Error("Invalid account setup response");
    state.accountName = response.accountName;
    state.emailHint = response.emailHint;
    state.role = response.role;
    state.invitationExpiresAt = response.expiresAt;
    state.codeExpiresAt = response.codeExpiresAt ?? null;
    state.phase = "resolved";
    return accountSetupSnapshot();
  });
}

export async function sendCapturedInvitationCode(api: ApiClient): Promise<{
  deliveryConfirmed: boolean;
  expiresAt: string;
}> {
  return oneFlight("send-code", async () => {
    const { state, appToken } = requireAppToken();
    if (state.phase !== "resolved" && state.phase !== "code_sent") {
      throw new Error("Invitation code cannot be sent now");
    }
    const value = await api.postPublic<unknown>("/public/account-setup/code/send", { appToken });
    if (!value || typeof value !== "object") throw new Error("Invalid account setup response");
    const response = value as Partial<ChallengeResponse>;
    if (
      typeof response.challengeId !== "string"
      || !UUID_PATTERN.test(response.challengeId)
      || typeof response.flowNonce !== "string"
      || !APP_TOKEN_PATTERN.test(response.flowNonce)
      || !validDateString(response.expiresAt)
      || typeof response.deliveryConfirmed !== "boolean"
    ) throw new Error("Invalid account setup response");
    state.challengeId = response.challengeId;
    state.flowNonce = response.flowNonce;
    state.exchangeSecret = null;
    state.providerTicket = null;
    state.setupSessionId = null;
    state.codeExpiresAt = response.expiresAt;
    state.providerExpiresAt = null;
    state.phase = "code_sent";
    return { deliveryConfirmed: response.deliveryConfirmed, expiresAt: response.expiresAt };
  });
}

export async function verifyCapturedInvitationCode(api: ApiClient, code: string): Promise<void> {
  await oneFlight("verify-code", async () => {
    const { state, appToken } = requireAppToken();
    if (
      state.phase !== "code_sent"
      || state.challengeId === null
      || state.flowNonce === null
      || !/^\d{6}$/.test(code)
    ) throw new Error("Invitation code is not available");
    const challengeId = state.challengeId;
    const value = await api.postPublic<unknown>("/public/account-setup/code/verify", {
      appToken,
      challengeId,
      flowNonce: state.flowNonce,
      code,
    });
    if (!value || typeof value !== "object") throw new Error("Invalid account setup response");
    const response = value as Partial<VerifiedResponse>;
    if (
      response.challengeId !== challengeId
      || typeof response.exchangeSecret !== "string"
      || !APP_TOKEN_PATTERN.test(response.exchangeSecret)
      || !validDateString(response.expiresAt)
    ) throw new Error("Invalid account setup response");
    state.flowNonce = null;
    state.exchangeSecret = response.exchangeSecret;
    state.codeExpiresAt = null;
    state.phase = "code_verified";
  });
}

export async function exchangeCapturedInvitationProof(api: ApiClient): Promise<void> {
  await oneFlight("exchange", async () => {
    const { state, appToken } = requireAppToken();
    if (
      state.phase !== "code_verified"
      || state.challengeId === null
      || state.exchangeSecret === null
    ) throw new Error("Invitation proof is not available");
    const value = await api.postPublic<unknown>("/public/account-setup/exchange", {
      appToken,
      challengeId: state.challengeId,
      exchangeSecret: state.exchangeSecret,
    });
    if (!value || typeof value !== "object") throw new Error("Invalid account setup response");
    const response = value as Partial<ExchangedResponse>;
    if (
      response.mode !== "password_setup"
      || typeof response.providerTicket !== "string"
      || response.providerTicket.length < 16
      || response.providerTicket.length > MAX_PROVIDER_TICKET_LENGTH
      || /[\u0000-\u001f\u007f]/.test(response.providerTicket)
      || !validDateString(response.expiresAt)
    ) throw new Error("Invalid account setup response");
    state.providerTicket = response.providerTicket;
    state.providerExpiresAt = response.expiresAt;
    state.phase = "ticket_issued";
  });
}

export async function createCapturedInvitationSession(signIn: TicketSignInLike): Promise<void> {
  await oneFlight("ticket-sign-in", async () => {
    const state = requireState();
    if (state.phase !== "ticket_issued" || state.providerTicket === null) {
      throw new Error("Invitation sign-in is not available");
    }
    const result = await signIn.create({ strategy: "ticket", ticket: state.providerTicket });
    if (result.status !== "complete" || !result.createdSessionId) {
      throw new Error("Invitation sign-in did not finish");
    }
    state.setupSessionId = result.createdSessionId;
    state.phase = "ticket_created";
  });
}

export async function activateCapturedInvitationSession(setActive: SetActiveLike): Promise<void> {
  await oneFlight("activate-session", async () => {
    const state = requireState();
    if (
      (state.phase !== "ticket_created" && state.phase !== "session_active")
      || state.setupSessionId === null
    ) throw new Error("Invitation session is not available");
    await setActive({ session: state.setupSessionId });
    state.phase = "session_active";
  });
}

export async function bindCapturedInvitationSession(api: ApiClient): Promise<void> {
  await oneFlight("bind-session", async () => {
    const state = requireState();
    if (
      state.phase !== "session_active"
      || state.appToken === null
      || state.challengeId === null
      || state.exchangeSecret === null
      || state.providerTicket === null
      || state.setupSessionId === null
    ) throw new Error("Invitation session is not ready");
    const value = await api.postInvitationAuth<unknown>("/public/account-setup/session/bind", {
      appToken: state.appToken,
      challengeId: state.challengeId,
      recoverySecret: state.exchangeSecret,
      providerTicket: state.providerTicket,
    });
    if (!value || typeof value !== "object") throw new Error("Invalid account setup response");
    const response = value as Partial<BoundResponse>;
    if (response.ok !== true || typeof response.accountName !== "string" || response.accountName.length === 0) {
      throw new Error("Invalid account setup response");
    }
    state.accountName = response.accountName;
    state.appToken = null;
    state.challengeId = null;
    state.flowNonce = null;
    state.exchangeSecret = null;
    state.providerTicket = null;
    state.providerExpiresAt = null;
    state.phase = "bound";
  });
}

export async function acceptCapturedInvitation(api: ApiClient): Promise<AcceptedSetupResponse> {
  return oneFlight("accept", async () => {
    const state = requireState();
    if (state.phase !== "bound" || state.setupSessionId === null) {
      throw new Error("Invitation setup is not bound");
    }
    const value = await api.postInvitationAuth<unknown>("/public/account-setup/accept", {});
    if (!value || typeof value !== "object") throw new Error("Invalid account setup response");
    const response = value as Partial<AcceptedSetupResponse>;
    if (
      response.ok !== true
      || typeof response.accountId !== "string"
      || typeof response.accountName !== "string"
      || !validRole(response.role)
      || typeof response.alreadyMember !== "boolean"
    ) throw new Error("Invalid account setup response");
    state.phase = "accepted";
    return response as AcceptedSetupResponse;
  });
}
